import React from 'react';
import { 
  User, 
  MapPin, 
  Phone, 
  Badge,
  Flame,
  Heart,
  Shield,
  LifeBuoy,
  Users
} from 'lucide-react';
import { Responder } from '../types';

interface ResponderCardProps {
  responder: Responder;
  className?: string;
}

export const ResponderCard: React.FC<ResponderCardProps> = ({ 
  responder, 
  className = '' 
}) => {
  const getCategoryConfig = () => {
    switch (responder.category) {
      case 'fire':
        return { 
          icon: Flame, 
          color: 'text-red-600 bg-red-100',
          label: 'Fire Department'
        };
      case 'medical':
        return { 
          icon: Heart, 
          color: 'text-red-500 bg-red-50',
          label: 'Medical Services'
        };
      case 'police':
        return { 
          icon: Shield, 
          color: 'text-blue-600 bg-blue-100',
          label: 'Police'
        };
      case 'rescue':
        return { 
          icon: LifeBuoy, 
          color: 'text-orange-600 bg-orange-100',
          label: 'Rescue Team'
        };
      case 'volunteer':
        return { 
          icon: Users, 
          color: 'text-green-600 bg-green-100',
          label: 'Volunteer'
        };
    }
  };

  const getStatusColor = () => {
    switch (responder.status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'en-route':
        return 'bg-orange-100 text-orange-800';
      case 'on-scene':
        return 'bg-purple-100 text-purple-800';
      case 'off-duty':
        return 'bg-gray-100 text-gray-800';
    }
  };

  const categoryConfig = getCategoryConfig();
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${categoryConfig.color}`}>
            <CategoryIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{responder.name}</h3>
            <p className="text-sm text-gray-600">{categoryConfig.label}</p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor()}`}>
          {responder.status}
        </span>
      </div>

      <div className="space-y-2 text-sm text-gray-600">
        {responder.badgeNumber && (
          <div className="flex items-center gap-2">
            <Badge className="w-4 h-4 text-gray-400" />
            <span>Badge: {responder.badgeNumber}</span>
          </div>
        )}
        {responder.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <span>{responder.phone}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span>
            {responder.location.address || 
             `${responder.location.latitude.toFixed(4)}, ${responder.location.longitude.toFixed(4)}`}
          </span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Available:</span>
          <span className={`font-medium ${responder.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
            {responder.isAvailable ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  );
};